import { createSetupTokenClient, getSetupToken } from "@/domain/_shared/auth/services/auth.service";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { useMutation } from "@tanstack/react-query";

import { generate2FASecretForSetupMutationDocument, generate2FASecretMutationDocument } from "./two-factor-auth.gql";

export interface Generate2FASecretOutput {
  secret: string;
  otpAuthUrl: string;
}

export interface UseGenerate2FASecretProps {
  onCompleted?: (data: Generate2FASecretOutput) => void;
  onError?: (e: Error) => void;
}

/**
 * Generates a TOTP secret for the current user. Auto-routes between the
 * authenticated and the forced-setup endpoints based on whether the user holds
 * a 2fa_setup token, so callers don't need to know which population they are.
 */
export const useGenerate2FASecret = ({ onCompleted, onError }: UseGenerate2FASecretProps = {}) => {
  const mutation = useMutation({
    mutationFn: async (): Promise<Generate2FASecretOutput> => {
      if (getSetupToken()) {
        const client = await createSetupTokenClient("generate2FASecretForSetup");
        const data = await client.request<{ generate2FASecretForSetup: Generate2FASecretOutput }>(
          generate2FASecretForSetupMutationDocument,
        );
        return data.generate2FASecretForSetup;
      }
      const client = createOperationClient("generate2FASecret");
      const data = await client.request(generate2FASecretMutationDocument, {});
      return data.generate2FASecret;
    },
    onSuccess: (data) => {
      onCompleted?.(data);
    },
    onError: (error) => {
      onError?.(error as Error);
    },
  });

  return {
    generateSecret: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
